import SocialIcon from '@atoms/Icon/SocialIcon';
import useLocale from '@hooks/useLocale';
import useTranslations from '@hooks/useTranslations';
import Section from '@molecules/Section/Section';
import SectionContent from '@molecules/Section/SectionContent';
import InteractiveMap from '@organisms/InteractiveMap/InteractiveMap';
import { MapSection as MapSectionProps } from '@type-declarations/sections';
import { getMapsUrl } from '@utils/externalUrl';
import getInternalRoute from '@utils/getInternalRoute';
import setExternalUrl from '@utils/setExternalUrl';
import clsx from 'clsx';
import Link from 'next/link';
import { useMemo } from 'react';

import styles from './MapSection.module.scss';

export default function MapSection({
  id,
  title,
  subtitle,
  text,
  cta,
  currentLocation,
  locations,
  information,
  size,
  isLocationPage = false,
}: MapSectionProps) {
  const t = useTranslations();
  const locale = useLocale();

  const mappedLocations = useMemo(() => {
    return locations
      .filter(location => !!location.coordinates)
      .map(location => ({
        ...location,
        icon:
          location.category?.[0]?.icon ||
          location.category?.[0]?.category?.[0]?.icon,
        pos: [
          Number(location.coordinates.latitude),
          Number(location.coordinates.longitude),
        ],
      }));
  }, [locations]);

  const showInformation =
    information?.address?.title ||
    (information?.address?.street &&
      information?.address?.streetNumber &&
      information?.address?.zipcode &&
      information?.address?.city) ||
    information?.phone ||
    information?.mail ||
    information?.website;

  return (
    <Section id={id}>
      <SectionContent
        title={title}
        subtitle={subtitle}
        text={text}
        cta={cta}
        addSpaceForButtons
      >
        <div className={styles.wrapper}>
          {showInformation && (
            <div className={styles.information}>
              <div className={styles.informationDetails}>
                {information.address.title &&
                  (currentLocation?.typename === 'LocationPage' &&
                  !isLocationPage ? (
                    <Link
                      href={getInternalRoute({
                        internalLink: currentLocation,
                        locale,
                        store: false,
                      })}
                      className={clsx(styles.informationLink, styles.underline)}
                    >
                      {information.address.title}
                    </Link>
                  ) : (
                    <p className="u-mb-0">{information.address.title}</p>
                  ))}
                {information.address.street && (
                  <p className="u-mb-0">
                    {information.address.street}{' '}
                    {information.address.streetNumber}
                    {information.address.streetNumberSuffix}
                  </p>
                )}
                {(information.address.zipcode || information.address.city) && (
                  <p className="u-mb-0">
                    {information.address.zipcode} {information.address.city}
                  </p>
                )}
                {information.phone && (
                  <a
                    href={`tel:${information.phone}`}
                    className={styles.informationLink}
                  >
                    {information.phone}
                  </a>
                )}
              </div>

              <div className={styles.informationLinks}>
                <a
                  href={getMapsUrl({
                    title: currentLocation?.title,
                    address: information.address,
                    coordinates: currentLocation?.pos
                      ? {
                          latitude: currentLocation.pos[0].toString(),
                          longitude: currentLocation.pos[1].toString(),
                        }
                      : undefined,
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.informationLink}
                >
                  {t.planRoute}
                </a>
                {information.website && (
                  <a
                    href={setExternalUrl(information.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.informationLink}
                  >
                    {t.visitWebsite}
                  </a>
                )}
                {information.mail && (
                  <a
                    href={`mailto:${information.mail}`}
                    className={styles.informationLink}
                  >
                    {information.mail}
                  </a>
                )}
              </div>

              <div className={styles.socials}>
                {information.socials?.map(social => (
                  <SocialIcon key={social.id} {...social} />
                ))}
              </div>
            </div>
          )}

          <InteractiveMap
            maxClusterRadius={40}
            currentLocation={currentLocation}
            locations={mappedLocations}
            size={size}
            className={styles.map}
            markerImageSize="xs"
            markerImageSizeBreakpoints={[]}
          />
        </div>
      </SectionContent>
    </Section>
  );
}
